import React, { useContext, useState } from "react"
import { Div, Row, Col, Text, Input } from "atomize"
import { profileContext } from "../providers/profile.provider"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { toast } from "react-toastify"
import Card from "../Card"
import Divider from "../atoms/Divider"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faCalendar,
	faCamera,
	faCalendarCheck,
	faGear,
	faLinkSlash,
	faSchool,
	faRightFromBracket,
	faPen,
	faCheck,
	faXmark,
	faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import TextInput from "../inputs/TextInput"
import CoachAvatar from "../atoms/CoachAvatar"
import ThemeSelector from "./ThemeSelector"
import LessonsViewSelector from "./LessonsViewSelector"
import SchemaSurfaceSelector from "./SchemaSurfaceSelector"
import OtherSchoolsSelector from "./OtherSchoolsSelector"
import MatchesSelector from "./MatchesSelector"
import { networkAwareMessage } from "../utils/network.utils"

const AccountCard = ({ schoolName, coachID, onLogout }) => {
	const { coach, disconnectGoogleCalendar, changeEmail } = useContext(profileContext)
	const Modal = useContext(modalContext)
	const [isLoading, setIsLoading] = useState(false)
	const [isEditingEmail, setIsEditingEmail] = useState(false)
	const [emailValue, setEmailValue] = useState(coach?.email ?? "")
	const [currentPassword, setCurrentPassword] = useState("")
	const [isSavingEmail, setIsSavingEmail] = useState(false)

	const isConnected = coach?.googleCalendar?.connected === true
	// il refresh token è scaduto o è stato revocato: serve un nuovo consenso
	const needsReconnect = coach?.googleCalendar?.needsReconnect === true

	const handleDisconnect = async () => {
		setIsLoading(true)
		try {
			await disconnectGoogleCalendar()
			toast.info("Google Calendar scollegato", { position: "top-center" })
		} catch (err) {
			toast.error(networkAwareMessage(err, "Errore durante la disconnessione"), {
				position: "top-center",
			})
		} finally {
			setIsLoading(false)
		}
	}

	const handleSaveEmail = async () => {
		if (!emailValue.includes("@")) {
			toast.error("Inserisci un'email valida!", { position: "top-center" })
			return
		}
		if (currentPassword === "") {
			toast.error("Inserisci la password attuale", { position: "top-center" })
			return
		}
		setIsSavingEmail(true)
		try {
			// l'email è anche la credenziale di accesso: la cambia il server,
			// su Firestore e su Firebase Auth insieme, dopo che chi la chiede
			// ha ridigitato la password
			await changeEmail(emailValue, currentPassword)
			toast.success("Email aggiornata! Ti abbiamo mandato un link per confermarla", {
				position: "top-center",
			})
			setIsEditingEmail(false)
			setCurrentPassword("")
		} catch (err) {
			toast.error(err.message || "Errore durante il salvataggio", {
				position: "top-center",
			})
		} finally {
			setIsSavingEmail(false)
		}
	}

	return (
		<Card bg={theme.colors.surface}>
			{/* Info account: la foto, e accanto chi sei. Toccando la foto si
			    apre il suo foglio (PictureModal), dove si cambia */}
			<Div d="flex" align="center" style={{ gap: "0.875rem" }} m={{ b: "0.75rem" }}>
				<button
					type="button"
					aria-label={coach?.photo ? "Cambia la tua foto" : "Aggiungi una foto"}
					onClick={() => Modal.open("picture", { kind: "coach" })}
					style={{
						position: "relative",
						display: "inline-flex",
						flexShrink: 0,
						padding: 0,
						border: 0,
						borderRadius: "50%",
						background: "none",
						cursor: "pointer",
					}}>
					<CoachAvatar coach={coach} size={64} />
					{/* la fotocamera dice che il tondo si tocca */}
					<span
						aria-hidden="true"
						style={{
							position: "absolute",
							right: "-2px",
							bottom: "-2px",
							width: "24px",
							height: "24px",
							borderRadius: "50%",
							background: theme.colors.primary,
							boxShadow: `0 0 0 3px ${theme.colors.surface}`,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}>
						<FontAwesomeIcon icon={faCamera} color={theme.colors.white} style={{ fontSize: "11px" }} />
					</span>
				</button>
				<Div style={{ flex: 1, minWidth: 0 }}>
					{/* Il nome, non l'identificativo: da quando ci si registra
					    liberamente quello è una sigla generata, che non dice niente
					    a chi la legge e non va mostrata in giro. */}
					<Text textWeight="bold" textSize="subheader">
						{coach?.coachName || coachID}
					</Text>
					<Div d="flex" align="center" style={{ gap: "0.35rem" }}>
						<FontAwesomeIcon
							icon={faSchool}
							size="xs"
							color={theme.colors.text_light}
						/>
						<Text textSize="caption" textColor={theme.colors.text_light}>
							{schoolName}
						</Text>
					</Div>

					{/* Email */}
					{isEditingEmail ? (
						<Div d="flex" align="center" style={{ gap: "0.5rem" }} m={{ t: "0.5rem" }}>
							<Div d="flex" flexDir="column" style={{ flex: 1, gap: "0.35rem", minWidth: 0 }}>
								<TextInput
									type="email"
									value={emailValue}
									handleChange={e => setEmailValue(e.target.value)}
									placeholder="es. mario@esempio.it"
									m="0"
									h="2.5rem"
									submitAction={handleSaveEmail}
								/>
								{/* chi cambia la chiave del recupero password deve
								    dimostrare di conoscere quella di adesso */}
								<TextInput
									type="password"
									value={currentPassword}
									handleChange={e => setCurrentPassword(e.target.value)}
									placeholder="password attuale"
									m="0"
									h="2.5rem"
									submitAction={handleSaveEmail}
								/>
							</Div>
							<Div d="flex" align="center" style={{ gap: "0.35rem", flexShrink: 0 }}>
								<Div
									cursor="pointer"
									p="0.4rem"
									rounded="circle"
									bg={
										isSavingEmail
											? theme.colors.gray_light
											: theme.colors.gunmetal_fill
									}
									onClick={!isSavingEmail ? handleSaveEmail : undefined}>
									<FontAwesomeIcon icon={faCheck} color="white" size="sm" />
								</Div>
								<Div
									cursor="pointer"
									p="0.4rem"
									rounded="circle"
									bg={theme.colors.gray_light}
									onClick={() => {
										setEmailValue(coach?.email ?? "")
										setCurrentPassword("")
										setIsEditingEmail(false)
									}}>
									<FontAwesomeIcon
										icon={faXmark}
										color={theme.colors.gunmetal}
										size="sm"
									/>
								</Div>
							</Div>
						</Div>
					) : (
						<Div
							d="flex"
							align="center"
							style={{ gap: "0.35rem" }}
							m={{ t: "0.1rem" }}
							cursor="pointer"
							onClick={() => setIsEditingEmail(true)}>
							<Text
								textSize="caption"
								textColor={
									coach?.email ? theme.colors.gunmetal : theme.colors.primary
								}>
								{coach?.email ?? "Aggiungi email per il recupero password"}
							</Text>
							<FontAwesomeIcon
								icon={faPen}
								size="xs"
								color={
									coach?.email ? theme.colors.text_light : theme.colors.primary
								}
							/>
						</Div>
					)}
				</Div>
			</Div>

			<Divider w="2px solid" borderColor={theme.colors.background} m={{ y: "1rem" }} />

			{/* Google Calendar */}
			<Div d="flex" align="center" justify="space-between">
				<Div d="flex" align="center" style={{ gap: "0.75rem" }}>
					<Div
						w="40px"
						h="40px"
						rounded="circle"
						bg={isConnected ? theme.colors.primary_light : theme.colors.gray_light}
						d="flex"
						align="center"
						justify="center"
						style={{ flexShrink: 0 }}>
						<FontAwesomeIcon
							icon={isConnected ? faCalendarCheck : faCalendar}
							color={isConnected ? theme.colors.primary : theme.colors.text_light}
						/>
					</Div>
					<Div>
						<Text textWeight="bold" textSize="subheader">
							Google Calendar
						</Text>
						<Text textSize="caption" textColor={theme.colors.text_light}>
							{isConnected
								? coach.googleCalendar.calendarName ?? "Calendario principale"
								: "Non collegato"}
						</Text>
					</Div>
				</Div>

				<Div d="flex" align="center" style={{ gap: "0.5rem" }}>
					<PrimaryButton
						m="0"
						p={{ x: "0.75rem" }}
						bg={theme.colors.gunmetal_fill}
						textColor="white"
						text={
							<Div d="flex" align="center" style={{ gap: "0.4rem" }}>
								<FontAwesomeIcon icon={faGear} size="sm" />
								<span>
									{isConnected
										? "Gestisci"
										: needsReconnect
										? "Ricollega"
										: "Collega"}
								</span>
							</Div>
						}
						action={() => Modal.open("googleCalendar")}
					/>
					{isConnected && (
						<SecondaryButton
							m="0"
							textColor={theme.colors.text_light}
							text={isLoading ? "..." : <FontAwesomeIcon icon={faLinkSlash} />}
							disabled={isLoading}
							action={handleDisconnect}
						/>
					)}
				</Div>
			</Div>

			{needsReconnect && (
				<Div
					d="flex"
					align="center"
					m={{ t: "0.75rem" }}
					p="0.6rem 0.75rem"
					rounded="10px"
					bg={theme.colors.primary_light}
					style={{ gap: "0.6rem" }}>
					<FontAwesomeIcon icon={faTriangleExclamation} color={theme.colors.warning} />
					<Text textSize="caption" textColor={theme.colors.gunmetal}>
						Il collegamento con Google è scaduto: le lezioni non vengono più
						sincronizzate. Premi <b>Collega</b> per ripristinarlo.
					</Text>
				</Div>
			)}

			<Divider w="2px solid" borderColor={theme.colors.background} m={{ y: "1rem" }} />

			{/* Aspetto */}
			<ThemeSelector />

			<Divider w="2px solid" borderColor={theme.colors.background} m={{ y: "1rem" }} />

			{/* Da dove si parte nelle lezioni */}
			<LessonsViewSelector />

			{/* Le ore negli altri circoli, per chi insegna in più di uno */}
			<OtherSchoolsSelector />

			{/* Se il "+" chiede anche "Gioco io" */}
			<MatchesSelector />

			<Divider w="2px solid" borderColor={theme.colors.background} m={{ y: "1rem" }} />

			{/* Su che campo si disegnano gli schemi */}
			<SchemaSurfaceSelector />

			<Divider w="2px solid" borderColor={theme.colors.background} m={{ y: "1rem" }} />

			{/* Logout */}
			<Div
				d="flex"
				align="center"
				justify="space-between"
				cursor="pointer"
				onClick={onLogout}
				p={{ y: "0.25rem" }}>
				<Text textSize="subheader" textColor={theme.colors.error}>
					Esci dall'account
				</Text>
				<FontAwesomeIcon icon={faRightFromBracket} color={theme.colors.error} />
			</Div>
		</Card>
	)
}

export default AccountCard
